import React, {useEffect, useState, forwardRef, useImperativeHandle, useCallback} from 'react'
import {
  useGetAccessToProductQuery,
  useGetProductPlanQuery,
  useGetProductQuotaQuery,
  useCancelSubscriptionMutation,
  useGetSubscriptionQuery,
  useGetProductQuery,
  useRecoverSubscriptionMutation,
} from '../../../../services/cellcast.v2'
import {useGetTrialStatusQuery} from '../../../../services/cellcast.v2'
import './BCardSubscriptionStatus.scss'
import {Card, Col, Row, Button, ProgressBar} from 'react-bootstrap'
import {useLocation, useNavigate} from 'react-router-dom'
import {useI18n} from '../../../Utils/_helpers/useI18n'
import {useBillingRefetch} from '../accounts/Billing/BillingContext'

import FeatureSplashPage from '../../../Components/FeatureSplashPage'
import FeatureActivatePage from '../../../Components/FeatureActivatePage'
import ChangePlanConfirmModal from '../accounts/Billing/ChangePlanConfirmModal'
import ChangePlanDrawer from '../accounts/Billing/ChangePlanDrawer'
import PlanPaymentForm from '../../../Components/PlanPaymentForm'
import CancelSubscriptionModal, {CancelFeedbackData} from './CancelSubscriptionModal'
import {toast} from 'react-toastify'
import {ProductPlan} from '../../../../services/cellcast.v2.types'
import {convertToMiliseconds} from '../contact-collection/utils'
import {formatTimestampToDate} from '../../../Utils/Constant/formatDayTime'
import BcardSplashScreen from './BcardSplashScreen'
import PaymentSuccessModal from './PaymentSuccessModal'
import { manageButtonText } from '../accounts/Billing/BillingSubscription'
import DemoPageLeadzApp from './DemoPageLeadzApp'

const BCardSubscriptionStatus = forwardRef<{reFetchAllData: () => Promise<void>}, {}>(
  (props, ref) => {
    const {t} = useI18n()
    const navigate = useNavigate()
    const location = useLocation()

    // Use billing refetch context (optional, undefined if not in provider)
    const billingRefetch = useBillingRefetch()

    const [showActivatePage, setShowActivatePage] = useState(false)
    const [showChangePlanConfirm, setShowChangePlanConfirm] = useState(false)
    const [showPlanDrawer, setShowPlanDrawer] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState<ProductPlan | null>(null)
    const [showPaymentForm, setShowPaymentForm] = useState(false)

    const [showCancelConfirm, setShowCancelConfirm] = useState(false)
    const [isCancelling, setIsCancelling] = useState(false)
    const [cancelError, setCancelError] = useState<string | null>(null)
    const [showPaymentSuccess, setShowPaymentSuccess] = useState(false)
    const [successPlan, setSuccessPlan] = useState<ProductPlan | null>(null)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showDemoPage, setShowDemoPage] = useState(false)
    const [cancelSubscription] = useCancelSubscriptionMutation()

    const {data: productData} = useGetProductQuery({
      productKey: 'bcard',
    })

    const productId = productData?.data?.product?._id || ''

    const {
      data: accessToProduct,
      refetch: refetchAccessToProduct,
      isUninitialized: accessUninitialized,
    } = useGetAccessToProductQuery({productId}, {skip: !productId})

    const {
      data: productPlansResponse,
      refetch: refetchProductPlans,
      isUninitialized: plansUninitialized,
    } = useGetProductPlanQuery({productId}, {skip: !productId})

    const {
      data: productQuotaResponse,
      refetch: refetchProductQuota,
      isUninitialized: quotaUninitialized,
    } = useGetProductQuotaQuery({productId}, {skip: !productId})

    const {
      data: subscriptionData,
      refetch: refetchSubscription,
      isUninitialized: subUninitialized,
    } = useGetSubscriptionQuery({productId}, {skip: !productId})

    const {
      data: trialStatusResp,
      isUninitialized: trialStatusUninitialized,
      refetch: refetchTrialStatus,
    } = useGetTrialStatusQuery({productId}, {skip: !productId})

    const [recoverSubscription, {isLoading: isRecovering}] = useRecoverSubscriptionMutation()
    const dataAccessToProduct = accessToProduct?.data

    const productPlans = productPlansResponse?.data?.plans || []
    const productQuota = productQuotaResponse?.data?.quota_usage
    const subscription = subscriptionData?.data?.subscriptions[0]
    // const trialStatus = trialStatusResp?.data?.trialStatus

    const currentPlan = productPlans.find((plan) => {
      if (!productQuota) return false
      return plan.quotaLimit === productQuota.quotaLimit
    })

    const usagePercentage = productQuota
      ? Math.min((productQuota.currentUsage / productQuota.quotaLimit) * 100, 100)
      : 0

    const getProgressVariant = () => {
      if (usagePercentage >= 90) return 'danger'
      if (usagePercentage >= 75) return 'warning'
      return 'primary'
    }

    useEffect(() => {
      if (!productId) return
      const refetchAllData = async () => {
        try {
          const tasks: Array<Promise<any>> = []
          if (!accessUninitialized) tasks.push(refetchAccessToProduct())
          if (!plansUninitialized) tasks.push(refetchProductPlans())
          if (!quotaUninitialized) tasks.push(refetchProductQuota())
          if (!subUninitialized) tasks.push(refetchSubscription())
          if (!trialStatusUninitialized) tasks.push(refetchTrialStatus())
          if (tasks.length) await Promise.all(tasks)
        } catch (error) {
          console.error('Failed to refetch data:', error)
        }
      }
      refetchAllData()
    }, [
      productId,
      accessUninitialized,
      plansUninitialized,
      quotaUninitialized,
      subUninitialized,
      trialStatusUninitialized,
      refetchAccessToProduct,
      refetchProductPlans,
      refetchProductQuota,
      refetchSubscription,
      refetchTrialStatus,
    ])

    useEffect(() => {
      const isActivate = location.pathname.endsWith('/wallet-card/subscription/activate')
      setShowActivatePage(isActivate)
    }, [location.pathname])

    const renderNextQuotaReset = () => {
      const resetDate = productQuota?.resetDate ?? 0
      const isCanceled = subscription?.cancelAtPeriodEnd
      const formattedDate = formatTimestampToDate(convertToMiliseconds(resetDate))

      if (isCanceled) {
        return (
          <>
            Next Quota Reset: Subscription canceled. Usage will continue until the end of the
            current period ({formattedDate}).
          </>
        )
      }

      return <>Next Quota Reset: {formattedDate}</>
    }

    const reFetchAllData = useCallback(async () => {
      await Promise.all([
        refetchAccessToProduct(),
        refetchProductPlans(),
        refetchProductQuota(),
        refetchSubscription(),
      ])

      // Trigger refetch in BillingSubscription if context is available
      if (billingRefetch) {
        billingRefetch.triggerRefetch()
      }
    }, [
      refetchAccessToProduct,
      refetchProductPlans,
      refetchProductQuota,
      refetchSubscription,
      billingRefetch,
    ])

    // Expose reFetchAllData function to parent component
    useImperativeHandle(
      ref,
      () => ({
        reFetchAllData,
      }),
      [reFetchAllData]
    )

    const handleRecoverSubscription = async () => {
      try {
        if (!subscription?._id) {
          throw new Error('Subscription ID not found. Please refresh the page and try again.')
        }

        const response = await recoverSubscription({
          subscriptionId: subscription._id,
        })

        if ('data' in response && response.data) {
          const successMessage = response.data.message || 'Subscription recovered successfully!'
          toast.success(successMessage)
          await reFetchAllData()
        } else if ('error' in response) {
          const errorMessage = extractErrorMessage(response)
          throw new Error(errorMessage)
        } else {
          throw new Error('Unexpected response format from server')
        }
      } catch (error: any) {
        console.error('Failed to recover subscription:', error)

        let errorMessage = 'Failed to recover subscription'

        if (error?.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error?.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message
        } else if (error?.message) {
          errorMessage = error.message
        }

        toast.error(errorMessage)
      }
    }
    const handleConfirmPlanChange = (applyNow: boolean) => {
      if (!selectedPlan) {
        setShowChangePlanConfirm(false)
        return
      }
      setShowChangePlanConfirm(false)
      setShowPaymentForm(true)
    }

    const extractErrorMessage = (response: any): string => {
      let errorMessage = 'Failed to cancel subscription'

      if (response.error && typeof response.error === 'object') {
        if (
          'data' in response.error &&
          response.error.data &&
          typeof response.error.data === 'object'
        ) {
          if (
            'error' in response.error.data &&
            typeof response.error.data.error === 'object' &&
            response.error.data.error &&
            'message' in response.error.data.error
          ) {
            errorMessage = String(response.error.data.error.message)
          } else if ('message' in response.error.data) {
            errorMessage = String(response.error.data.message)
          }
        }
      }

      return errorMessage
    }

    const handleCancelSubscription = async (feedbackData: CancelFeedbackData): Promise<boolean> => {
      const {selectedReasonIds, customReason} = feedbackData
      try {
        setCancelError(null)
        setIsCancelling(true)

        if (!subscription?.stripeSubscriptionId) {
          toast.error('Subscription stripe id not found')
          setCancelError('Subscription ID not found')
          return false
        }

        const response = await cancelSubscription({
          stripeSubscriptionId: subscription?.stripeSubscriptionId,
          selectedReasonIds,
          customReason,
        })

        if ('data' in response) {
          toast.success('Subscription cancelled successfully')

          await reFetchAllData()

          return true
        } else {
          const errorMessage = extractErrorMessage(response)
          setCancelError(errorMessage)
          toast.error(errorMessage)
          return false
        }
      } catch (error: any) {
        const errorMsg = error?.message || 'Failed to cancel subscription'
        setCancelError(errorMsg)
        toast.error(errorMsg)
        return false
      } finally {
        setIsCancelling(false)
      }
    }

    // Show demo page after payment success
    if (showDemoPage) {
      return <DemoPageLeadzApp />
    }

    if (dataAccessToProduct?.hasAccess === false) {
      if (showActivatePage) {
        return (
          <FeatureActivatePage
            title={t('MOBILE_WALLET_CARD.ACTIVATE.TITLE') || 'Choose your plan'}
            plans={productPlans}
            onSelectPlan={async (plan: ProductPlan) => {
              setShowPaymentSuccess(true)
              setSuccessPlan(plan)
              await reFetchAllData()

              setShowActivatePage(false)
            }}
            showBackButton={true}
          />
        )
      }

      return (
        <FeatureSplashPage
          title='Activate Your AI LeadzApp'
          description={<BcardSplashScreen />}
          product={dataAccessToProduct?.product}
          onActivate={() => {
            setShowActivatePage(true)
            navigate('/wallet-card/subscription/activate')
          }}
        />
      )
    }

    if (showActivatePage) {
      return (
        <FeatureActivatePage
          title={t('MOBILE_WALLET_CARD.ACTIVATE.TITLE') || 'Choose your plan'}
          plans={productPlans}
          onSelectPlan={async (plan: ProductPlan) => {
            if (plan?.name && currentPlan?.name && plan.name === currentPlan.name) {
              toast.info('You are already on this plan')
              return
            }

            setSelectedPlan(plan)
            setShowPaymentSuccess(true)
            setShowActivatePage(false)
          }}
          showBackButton={true}
        />
      )
    }

    return (
      <>
        <div className='card mb-5 mb-xl-10'>
          <div className='card-header border-0 pt-5'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>LeadzApp Status</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Current plan and usage information
              </span>
            </h3>
          </div>

          <div className='card-body py-3'>
            <div className='mb-8'>
              <h4 className='fs-4 fw-bold mb-4'>Current Plan</h4>
              <Row className='g-5 g-xl-8'>
                <Col xl={6}>
                  <Card className='card-stretch mb-5 mb-xxl-8 h-100'>
                    <Card.Body className='p-6'>
                      <div className='d-flex flex-column h-100'>
                        <div className='mb-7'>
                          <div className='d-flex align-items-center'>
                            <div className='symbol symbol-50px me-5'>
                              <div className={`symbol-label bg-light-${getProgressVariant()}`}>
                                <i
                                  className={`bi bi-credit-card-2-front text-${getProgressVariant()} fs-2x`}
                                ></i>
                              </div>
                            </div>
                            <div>
                              <h4 className='fw-bold text-gray-800 mb-0'>
                                {currentPlan?.name || 'Free Plan'}
                              </h4>
                              <div className='fs-6 text-gray-600'>
                                {currentPlan?.price ? `$${currentPlan.price}/month` : 'Free'}
                              </div>
                              {subscription && (
                                <div className='mt-3 d-flex flex-wrap gap-2'>
                                  {subscription.isTrial && (
                                    <span className='badge badge-light-primary fw-semibold'>
                                      Trialing
                                    </span>
                                  )}
                                  {subscription.cancelAtPeriodEnd ? (
                                    <span className='badge badge-light-danger fw-semibold'>
                                      Canceled on{' '}
                                      {formatTimestampToDate(
                                        convertToMiliseconds(subscription.cancelAt || 0)
                                      )}
                                    </span>
                                  ) : (
                                    <span className='badge badge-light-success fw-semibold'>
                                      Active
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className='mb-6'>
                          <div className='d-flex align-items-center justify-content-between mb-2'>
                            <span className='fw-semibold text-gray-600'>Quota Usage</span>
                            <span className='fw-bold text-gray-800'>
                              {productQuota?.currentUsage || 0} / {productQuota?.quotaLimit || 0}
                            </span>
                          </div>
                          <ProgressBar
                            now={usagePercentage}
                            variant={getProgressVariant()}
                            className='h-8px'
                          />
                        </div>

                        <div className='mb-6'>
                          <h5 className='fw-semibold text-gray-800 mb-3'>Plan Features</h5>
                          <div className='d-flex flex-column'>
                            {currentPlan?.features?.map((feature, index) => (
                              <div key={index} className='d-flex align-items-center mb-2'>
                                <span className='bullet bullet-dot bg-success me-2'></span>
                                <span className='text-gray-600'>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {productQuota?.resetDate && (
                          <div className='d-flex align-items-center mt-auto'>
                            <i className='bi bi-calendar-check fs-4 text-primary me-2'></i>
                            <span className='text-gray-700'>{renderNextQuotaReset()}</span>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col xl={6}>
                  <Card className='card-stretch mb-5 mb-xxl-8'>
                    <Card.Body className='p-6'>
                      <h5 className='fw-bold text-gray-800 mb-4'>Quick Actions</h5>

                      <div className='d-flex flex-column gap-3'>
                        <Button
                          variant='light-primary'
                          className='text-start py-3'
                          onClick={(e) => {
                            e.preventDefault()
                            setShowPlanDrawer(true)
                          }}
                        >
                          <i className='bi bi-arrow-left-right fs-3 me-3'></i>
                          Change Plan
                        </Button>

                        <Button
                          variant='light-primary'
                          className='text-start py-3'
                          onClick={() => navigate('/wallet-card')}
                        >
                          <i className='bi bi-grid-1x2 fs-3 me-3'></i>
                          Manage LeadzApp
                        </Button>

                        <Button
                          variant='light-info'
                          className='text-start py-3'
                          onClick={() => navigate('/report-wallet-card')}
                        >
                          <i className='bi bi-bar-chart fs-3 me-3'></i>
                          View Analytics
                        </Button>

                        {subscription?.cancelAtPeriodEnd && (
                          <Button
                            variant='light-success'
                            className='text-start py-3'
                            disabled={isRecovering}
                            onClick={(e) => {
                              e.preventDefault()
                              handleRecoverSubscription()
                            }}
                          >
                            {isRecovering ? (
                              <>
                                <span
                                  className='spinner-border spinner-border-sm me-3'
                                  role='status'
                                  aria-hidden='true'
                                ></span>
                                Recovering...
                              </>
                            ) : (
                              <>
                                <i className='bi bi-heart-pulse fs-3 me-3'></i>
                                Recover Subscription
                              </>
                            )}
                          </Button>
                        )}
                        {(currentPlan?.price ?? 0) > 0 && !subscription?.cancelAtPeriodEnd && (
                          <Button
                            variant='light-danger'
                            className='text-start py-3'
                            onClick={() => setShowCancelConfirm(true)}
                          >
                            <i className='bi bi-x-circle fs-3 me-3'></i>
                            Cancel Subscription
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        </div>

        <ChangePlanConfirmModal
          show={showChangePlanConfirm}
          onClose={async () => {
            setShowChangePlanConfirm(false)
            await reFetchAllData()
          }}
          onConfirm={handleConfirmPlanChange}
          currentPlan={currentPlan || null}
          newPlan={selectedPlan || null}
          isUpdate={true}
          subscriptionId={subscription?.stripeSubscriptionId || ''}
          nextBillingDate={formatTimestampToDate(
            convertToMiliseconds(subscription?.currentPeriodEnd || 0)
          )}
        />

        <ChangePlanDrawer
          show={showPlanDrawer}
          onClose={() => {
            setShowPlanDrawer(false)
            setShowChangePlanConfirm(false)
            setShowPaymentForm(false)
          }}
          onBack={() => {
            setShowPlanDrawer(false)
            setShowChangePlanConfirm(false)
            setShowPaymentForm(false)
          }}
          plans={productPlans}
          currentPlanName={currentPlan?.name}
          currentPlan={currentPlan}
          title={'Change AI B-Card Plan'}
          currentPlanLabel={`${currentPlan?.name} — $${currentPlan?.price}/mo`}
          nextBillDateLabel={formatTimestampToDate(
            convertToMiliseconds(subscription?.currentPeriodEnd || 0)
          )}
          estimatedNextPaymentLabel={
            selectedPlan?.price
              ? `$${Math.floor(selectedPlan.price)}.00`
              : currentPlan?.price
              ? `$${Math.floor(currentPlan.price)}.00`
              : '$0.00'
          }
          onUpdate={async (plan) => {
            if (!plan) return
            setSelectedPlan(plan)
            setShowPlanDrawer(false)
          }}
          onDataRefresh={async () => {
            setShowSuccessModal(true)
            setShowPlanDrawer(false)
            await reFetchAllData()
          }}
          subscription={subscription}
          productQuota={productQuotaResponse?.data || undefined}
        />

        <PaymentSuccessModal
          show={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false)
            navigate('/wallet-card/subscription/demo-leadz-app')
          }}
          plan={selectedPlan || null}
          description={`Click on the button below to start creating your digital business cards.`}
          onGoToSubscriptions={() => {
            navigate('/card-templates')
          }}
          titleGoto={manageButtonText(selectedPlan)}
        />

        <CancelSubscriptionModal
          show={showCancelConfirm}
          onHide={() => setShowCancelConfirm(false)}
          onConfirm={handleCancelSubscription}
          isCancelling={isCancelling}
          cancelError={cancelError || undefined}
          currentPlan={currentPlan}
          nextBillingDate={formatTimestampToDate(
            convertToMiliseconds(productQuota?.resetDate || 0)
          )}
          onAfterSuccessClose={async () => {
            await reFetchAllData()
            setShowCancelConfirm(false)
          }}
        />

        <PlanPaymentForm
          show={showPaymentForm}
          onClose={() => {
            setShowPaymentForm(false)
          }}
          plan={selectedPlan}
          onPaymentSuccess={() => {}}
          isUpdate={false}
        />

        <PaymentSuccessModal
          show={showPaymentSuccess}
          onClose={() => {
            setShowPaymentSuccess(false)
            setSuccessPlan(null)
            setShowDemoPage(true)
          }}
          plan={successPlan}
          description={`Click on the button below to start creating your digital business cards.`}
          onGoToSubscriptions={() => {
            setShowPaymentSuccess(false)
            setSuccessPlan(null)
            setShowDemoPage(true)
          }}
          titleGoto={manageButtonText(selectedPlan)}
          hideActions={true}
        />
      </>
    )
  }
)

BCardSubscriptionStatus.displayName = 'BCardSubscriptionStatus'

export default BCardSubscriptionStatus
